import {html, nothing} from "lit";
import {createRef, ref, Ref} from "lit/directives/ref.js";
import {customElement, property} from "lit/decorators.js";
import {MobxLitElement} from "@adobe/lit-mobx";

import Utils from "../../main/utils/index.js";
import Data from "../../main/data/index.js";
import {Select, SelectOption} from "../common/controls-inputs/Select.js";
import {projectUuid} from "../../main/data/model/index.js";
import {attachModal, MODALS} from "../common/modals.js";

const OPTION_CREATE_PROJECT = "__create_project__";

@customElement("pn-project-selector")
export class ProjectSelector extends MobxLitElement {
	select: Ref<Select> = createRef<Select>();

	@property({type: Boolean}) isUnsaved = false;

	protected createRenderRoot(): HTMLElement | DocumentFragment {
		return this;
	}

	onClickChangeProject = (uuid: projectUuid) => {
		if (uuid === OPTION_CREATE_PROJECT) {
			if (!Data.app.me?.email)
				// We want users to create an account (or log in) before they can add a second project
				attachModal(MODALS.INT_LOGIN);
			else
				attachModal(MODALS.CREATE_PROJECT);

			// Change the selected project in the menu back to the project it
			// was on before clicking the "Create project" option
			this.select.value?.setSelectedOption(Data.app.project?.uuid);
		} else
			Data.app.setCurrentProject(uuid);
	};

	render() {
		if (Data.app.acceptableProjects.length === 0)
			return nothing;

		const options = Data.app.acceptableProjects.map(project => ({
			label: project.name,
			value: project.uuid as string
		}));

		// Only add create project option if the account has general editing access
		if (Utils.Features.getSupportLevelData(Data.app.me).supportLevel !== "read") {
			options.push({
				label: "+ Create New Project...",
				value: OPTION_CREATE_PROJECT
			});
		}

		const selectedProjectUuid = Data.app.project?.uuid;
		if (selectedProjectUuid)
			this.select.value?.setSelectedOption(selectedProjectUuid);
		const selectedProjectOpt = options.find(p => p.value === selectedProjectUuid);

		// TODO: placeholder (see pn-browser-ext)
		return html`
			<pn-select
				.aria-label="${"Project selection"}"
				id="project-select"
				.onSelectCallback="${this.onClickChangeProject}"
				.options="${options}"
				.selectedOption=${selectedProjectOpt}
				.tooltipOptions=${{
					disabled:        false,
					tooltipPosition: "left"
				}}
				.customRenderer=${(option: SelectOption) => {
					const showUnsaved = this.isUnsaved && option.value !== OPTION_CREATE_PROJECT;
					const unsaved = html` <span class="warning">[UNSAVED]</span>`;
					return html`${option.label}${showUnsaved ? unsaved : nothing}`;
				}}
				${ref(this.select)}
			/>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pn-project-selector": ProjectSelector;
	}
}
